import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { osmApi } from "../osmApi";

const LABELS = {
  upload: "Uploaded",
  save: "Saved",
  submit: "Submitted",
  reopen: "Reopened",
};

function changeLines(entry) {
  const diff = entry.diff || entry.diffs || entry.changes || {};
  const questions = diff.questions || diff.marks || {};
  const lines = Object.entries(questions).map(([key, change]) => {
    const work = change?.work ? ` (${change.work})` : "";
    return `Q${key}: ${change?.from ?? "–"} → ${change?.to ?? "–"}${work}`;
  });

  const added = diff.annotations?.added;
  const removed = diff.annotations?.removed;
  if (added || removed)
    lines.push(`Annotations: +${added || 0} / -${removed || 0}`);
  return lines;
}

export default function HistoryDrawer({ open, scriptId, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");

    osmApi
      .getAudit(scriptId)
      .then((data) => {
        if (!cancelled) setEntries([...data].reverse());
      })
      .catch(() => {
        if (!cancelled) setError("Could not load history.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, scriptId]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 340, maxWidth: "100vw", p: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h6" fontWeight={800}>
            History
          </Typography>
          <IconButton aria-label="Close history" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {loading && <CircularProgress size={24} />}
        {error && <Typography color="error">{error}</Typography>}
        {!loading && !error && entries.length === 0 && (
          <Typography color="text.secondary">No history yet.</Typography>
        )}

        {entries.map((entry, index) => {
          const time = entry.createdAt || entry.timestamp || entry.at;
          const person = entry.examiner || entry.by || entry.user;
          return (
            <Box key={entry._id || index} py={1.25}>
              <Typography variant="body2" fontWeight={700}>
                {LABELS[entry.action] || entry.action}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {[person, time && new Date(time).toLocaleString()]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
              {changeLines(entry).map((line) => (
                <Typography key={line} variant="body2" sx={{ mt: 0.5 }}>
                  {line}
                </Typography>
              ))}
              {entry.reason && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Reason: {entry.reason}
                </Typography>
              )}
              <Divider sx={{ mt: 1.25 }} />
            </Box>
          );
        })}
      </Box>
    </Drawer>
  );
}
